import { Check } from "lucide-react"

import lockupDark from "@/images/tuw-logo-white.png"

import { Avatar } from "@/components/tuw/Avatar"
import BrandLockup from "@/components/tuw/auth/BrandLockup"
import { CsrfField } from "@/components/tuw/auth/fields"
import { cn } from "@/lib/utils"

// The three onboarding steps in order (design/spec/auth/onboarding.html
// `rail()`); the done screen shows all three finished.
const STEPS = [
  ["Verify your email", "Enter the 4-digit code we sent you"],
  ["Usage agreement", "A few ground rules for the platform"],
  ["Finish your profile", "Name, address and phone for your orders"],
]

// The onboarding layout: a dark left rail with the lockup, the step list and
// the signed-in customer, beside the step's own column. Below 900px the rail
// gives way to a slim header carrying the lockup and the step's kicker. Rendered on
// the clean-slate Inertia layout, never inside AppShell: the customer has not
// reached the app yet. `step` is 1, 2, 3 or "done".
// The rail's heading by `rail` (OnboardingPages#onboarding_rail): today's three
// steps; `twoSteps`, a new customer whose profile waits until they post or pay
// (new-flow-wording §25); `beforePosting`, the profile asked by today's posting
// form (§26). The highlighted words are the design's red ones.
const RAILS = {
  default: { lead: "Three quick steps before ", mark: "your dashboard.", sub: "Takes about two minutes. You only do this once." },
  twoSteps: { lead: "Two steps before ", mark: "your dashboard.", sub: "Verify email, then accept the usage agreement." },
  beforePosting: { lead: "Finish your profile ", mark: "before posting.", sub: "Saving returns you to the posting form." },
}

// The small label over a step's title, by rail.
export function stepKicker(step, rail) {
  if (rail === "beforePosting") return "Before posting"
  return `Step ${step} of ${rail === "twoSteps" ? 2 : 3}`
}

export default function OnboardingLayout({ step, rail, email, initials, signOut, narrow = false, children }) {
  const steps = rail === "twoSteps" ? STEPS.slice(0, 2) : STEPS
  const current = step === "done" ? steps.length : step - 1
  const progress = step === "done" ? `Step ${steps.length} of ${steps.length}` : stepKicker(step, rail)
  const heading = RAILS[rail] ?? RAILS.default

  return (
    <div className="grid min-h-screen grid-cols-[300px_minmax(0,1fr)] bg-background text-foreground max-[900px]:grid-cols-1">
      <aside className="sticky top-0 flex h-screen flex-col gap-9 bg-[image:var(--railbar-bg)] px-9 pt-9 pb-8 text-railbar-foreground max-[900px]:hidden dark:border-r dark:border-border">
        {/* The rail is dark in both themes, so it always carries the light-on-dark lockup. */}
        <img src={lockupDark} alt="dibbly | The Urban Writers" className="block h-auto w-full max-w-[200px]" />

        <div>
          <h2 className="font-display text-[22px] font-bold leading-[1.2] tracking-[-0.5px] [text-wrap:pretty]">
            {heading.lead}<em className="not-italic text-primary">{heading.mark}</em>
          </h2>
          <p className="mt-2 text-[13.5px] leading-[1.55] text-railbar-foreground/[0.62]">{heading.sub}</p>
        </div>

        <ol className="flex flex-col">
          {steps.map(([title, detail], index) => {
            const state = index < current ? "done" : index === current ? "now" : "todo"
            return (
              <li
                key={title}
                data-state={state}
                className={cn(
                  "flex gap-3.5 border-t border-railbar-foreground/[0.12] py-3.5 text-[13.5px] font-medium last:border-b last:border-railbar-foreground/[0.12]",
                  state === "now" && "text-railbar-foreground",
                  state === "done" && "text-railbar-foreground/80",
                  state === "todo" && "text-railbar-foreground/55"
                )}
              >
                <i
                  className={cn(
                    "flex size-6 shrink-0 items-center justify-center rounded-full border-[1.5px] font-display text-[11.5px] font-bold not-italic",
                    state === "now" && "border-primary bg-primary text-primary-foreground",
                    state === "done" && "border-transparent bg-done/25",
                    state === "todo" && "border-railbar-foreground/30"
                  )}
                >
                  {state === "done" ? <Check className="size-3 text-railbar-foreground" strokeWidth={3} /> : index + 1}
                </i>
                <span>
                  <b className="block font-semibold">{title}</b>
                  <small className="mt-0.5 block text-[12px] leading-[1.4] text-railbar-foreground/45">{detail}</small>
                </span>
              </li>
            )
          })}
        </ol>

        <div className="mt-auto text-[12px] leading-[1.5] text-railbar-foreground/50">
          <div className="flex items-center gap-2.5 text-[12.5px] text-railbar-foreground/70">
            <Avatar initials={initials} name={email} size={30} />
            <span className="min-w-0 flex-1 truncate">{email}</span>
            <SignOut signOut={signOut} />
          </div>
          <div className="mt-3.5">
            Stuck?{" "}
            <a href="mailto:support@dibbly.com" className="font-semibold text-railbar-foreground/85 underline underline-offset-2">
              support@dibbly.com
            </a>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-col">
        <header className="hidden items-center justify-between border-b border-border px-5 py-4 max-[900px]:flex">
          <BrandLockup className="h-6" />
          <span className="text-[12px] font-bold text-ink-3">{progress}</span>
        </header>
        <main className="flex flex-1 justify-center px-12 pt-[52px] pb-20 max-[900px]:px-6 max-[900px]:pt-7 max-[900px]:pb-[100px] max-[560px]:px-[18px] max-[560px]:pt-[22px]">
          <div className={cn("flex w-full flex-col gap-6", narrow ? "max-w-[480px]" : "max-w-[640px]")}>{children}</div>
        </main>
      </div>
    </div>
  )
}

// Ending the session is a full navigation: Devise's DELETE /users/sign_out
// lands on the sign in page with its notice. A masquerading admin is handed
// the masquerade's own exit link instead.
function SignOut({ signOut }) {
  const className = "font-semibold text-railbar-foreground/55 hover:text-railbar-foreground"
  if (signOut?.href) {
    return (
      <a href={signOut.href} className={className}>
        Sign out
      </a>
    )
  }
  return (
    <form method="post" action={signOut?.action ?? "/users/sign_out"} className="shrink-0">
      <input type="hidden" name="_method" value="delete" />
      <CsrfField />
      <button type="submit" className={className}>
        Sign out
      </button>
    </form>
  )
}

// The step column's title block (design `.kicker` + `.ptitle`).
export function StepTitle({ kicker, title, children }) {
  return (
    <div>
      {kicker && <div className="text-[11.5px] font-bold tracking-[0.1em] text-ink-3 uppercase">{kicker}</div>}
      <h1 className="mt-1.5 font-display text-[27px] font-bold leading-[1.15] tracking-[-0.6px] max-[560px]:text-[24px]">
        {title}
      </h1>
      {children && (
        <p className="mt-2 max-w-[56ch] text-[14px] leading-[1.55] text-muted-foreground [text-wrap:pretty] [&_b]:font-semibold [&_b]:text-foreground">
          {children}
        </p>
      )}
    </div>
  )
}
